import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import MilestoneReportApproval from '../components/milestones/MilestoneReportApproval';

/**
 * MilestoneReportReviewPage
 *
 * Landing page for the milestone-report notification links
 * (/milestone-reports/:id/review and /milestone-reports/:id). The review
 * dialog itself is the existing MilestoneReportApproval component; this page
 * exists so those deep links resolve instead of 404ing — before it, clients
 * had NO surface to open a submitted report from the notification.
 *
 * After any decision (or close) the user lands on the project page, where the
 * client releases the stage payment once the report is approved.
 */
const MilestoneReportReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getApiClient } = useAuth();

  const [projectId, setProjectId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiClient = getApiClient();
        const res = await apiClient.get(`/milestone-reports/${id}`);
        if (!cancelled) setProjectId(res.data?.data?.project_id || null);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.status === 404
            ? 'This milestone report was not found, or you do not have access to it.'
            : 'Failed to load the milestone report. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const goToProject = () => {
    if (projectId) navigate(`/projects/${projectId}`);
    else navigate('/dashboard');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 6, px: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </Box>
    );
  }

  return (
    <MilestoneReportApproval
      open
      reportId={id}
      userRole={user?.role === 'contractor' ? 'contractor' : 'client'}
      onClose={goToProject}
      onApproved={goToProject}
      onChangesRequested={goToProject}
      onRejected={goToProject}
    />
  );
};

export default MilestoneReportReviewPage;
